/**
 * Currency formatting options
 */
export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' // Future extensibility
export type CurrencyStyle = 'smart' | 'exact' | 'compact'

/**
 * Smart currency formatting with progressive detail levels
 * 
 * @param cents - Amount in cents (e.g., 27000 for $270.00)
 * @param currency - Currency code (default: 'USD')
 * @param style - Formatting style (default: 'smart')
 * @returns Formatted currency string
 * 
 * Smart formatting rules:
 * - < $500: $270 (exact amount with commas)
 * - $500 - $9,999: $1.5k (1 decimal place, no .0)
 * - $10k - $499,999: $260k (no decimals)
 * - $500k - $9,999,999: $1.2m (1 decimal place, no .0)
 * - $10M+: $12m (no decimals)
 * 
 * Examples:
 * - 27000 → "$270"
 * - 150000 → "$1.5k"
 * - 1500000 → "$15k"
 * - 120000000 → "$1.2m"
 * - 12000000000 → "$120m"
 */
export function formatSmartCurrency(cents: number, _currency: CurrencyCode = 'USD'): string {
	if (cents === 0) return '$0'
	
	const dollars = Math.abs(cents) / 100
	const isNegative = cents < 0
	const prefix = isNegative ? '-$' : '$'
	
	// Handle very large amounts (over $100M) - just show millions
	if (dollars >= 100_000_000) {
		const millions = Math.round(dollars / 1_000_000)
		return `${prefix}${millions}m`
	}
	
	// $10M - $99.9M: Show millions with 1 decimal if needed
	if (dollars >= 10_000_000) {
		const millions = dollars / 1_000_000
		const rounded = Math.round(millions * 10) / 10
		return rounded % 1 === 0 
			? `${prefix}${Math.round(rounded)}m`
			: `${prefix}${rounded}m`
	}
	
	// $500k - $9.9M: Show millions with 1 decimal if needed
	if (dollars >= 500_000) {
		const millions = dollars / 1_000_000
		const rounded = Math.round(millions * 10) / 10
		return rounded % 1 === 0 
			? `${prefix}${Math.round(rounded)}m`
			: `${prefix}${rounded}m`
	}
	
	// $10k - $499.9k: Show thousands with no decimals
	if (dollars >= 10_000) {
		const thousands = Math.round(dollars / 1_000)
		return `${prefix}${thousands}k`
	}
	
	// $500 - $9.9k: Show thousands with 1 decimal if needed
	if (dollars >= 500) {
		const thousands = dollars / 1_000
		const rounded = Math.round(thousands * 10) / 10
		return rounded % 1 === 0 
			? `${prefix}${Math.round(rounded)}k`
			: `${prefix}${rounded}k`
	}
	
	// < $500: Show exact amount with commas
	return `${prefix}${Math.round(dollars).toLocaleString()}`
}

/**
 * Alternative currency formatting for different contexts
 * 
 * @param cents - Amount in cents
 * @param currency - Currency code (default: 'USD')
 * @param style - Formatting style
 * @returns Formatted currency string
 */
export function formatCurrency(
	cents: number, 
	currency: CurrencyCode = 'USD', 
	style: CurrencyStyle = 'smart'
): string {
	switch (style) {
		case 'smart':
		case 'compact':
			return formatSmartCurrency(cents, currency)
		case 'exact':
			if (cents === 0) return '$0'
			const dollars = Math.abs(cents) / 100
			const isNegative = cents < 0
			const prefix = isNegative ? '-$' : '$'
			return `${prefix}${Math.round(dollars).toLocaleString()}`
		default:
			return formatSmartCurrency(cents, currency)
	}
}

/**
 * Legacy function for backward compatibility
 * @deprecated Use formatSmartCurrency instead
 */
export function formatValue(cents: number): string {
	return formatSmartCurrency(cents)
}
